import React, { useState, useEffect, useRef } from "react";
import { X, Plus, Trash2, Paperclip, User, Mail, Phone, Briefcase, FileText, Loader2, Download } from "lucide-react";
import { useAuth } from "../../context/AuthContext";

export default function PartnerModal({ 
  partner, 
  categories, 
  agreements = [],
  onOpenAgreement,
  onClose, 
  onSave, 
  uploadPartnerAttachment, 
  deletePartnerAttachment 
}) {
  const { currentUser } = useAuth();
  const isEdit = !!partner;
  
  const linkedPlatformAgreements = (partner && agreements) ? agreements.filter(a => a.partnerId === partner.id) : [];
  
  const [name, setName] = useState("");
  const [categoryName, setCategoryName] = useState("");
  const [pocs, setPocs] = useState([]);
  const [attachments, setAttachments] = useState([]);
  
  // Drag-and-drop & Upload states
  const [isDragging, setIsDragging] = useState(false);
  const [uploadingFiles, setUploadingFiles] = useState([]);
  const [error, setError] = useState("");

  useEffect(() => {
    if (partner) {
      setName(partner.name || "");
      setCategoryName(partner.categoryName || "");
      setPocs(partner.pocs || []);
      setAttachments(partner.attachments || []);
    } else {
      setName("");
      setCategoryName(categories[0]?.name || "");
      setPocs([{ name: "", title: "", email: "", phone: "" }]);
      setAttachments([]);
    }
  }, [partner, categories]);

  // Simulated scanner for attachments stuck on "Scanning..."
  useEffect(() => {
    if (attachments && attachments.length > 0) {
      const unscanned = attachments.filter(att => att.virusFree === undefined);
      if (unscanned.length > 0) {
        const timer = setTimeout(() => {
          const updated = attachments.map(att => {
            if (att.virusFree === undefined) {
              return { ...att, virusFree: true };
            }
            return att;
          });
          setAttachments(updated);
        }, 2000);
        return () => clearTimeout(timer);
      }
    }
  }, [attachments]);

  const handleAddPoc = () => {
    setPocs([...pocs, { name: "", title: "", email: "", phone: "" }]);
  };

  const handleRemovePoc = (index) => {
    const updated = [...pocs];
    updated.splice(index, 1);
    setPocs(updated);
  };

  const handlePocChange = (index, field, value) => {
    const updated = [...pocs];
    updated[index][field] = value;
    setPocs(updated);
  };

  // Drag and drop handlers
  const handleDragOver = (e) => {
    e.preventDefault();
    setIsDragging(true);
  };

  const handleDragLeave = () => {
    setIsDragging(false);
  };

  const handleDrop = async (e) => {
    e.preventDefault();
    setIsDragging(false);
    const files = Array.from(e.dataTransfer.files);
    if (files.length > 0) {
      await handleFilesUpload(files);
    }
  };

  const handleFileSelect = async (e) => {
    const files = Array.from(e.target.files);
    if (files.length > 0) {
      await handleFilesUpload(files);
    }
  };

  const handleFilesUpload = async (files) => {
    // Generate a temporary partner ID if it's a new partner, to upload files to a unique location
    const partnerId = partner?.id || `temp-${Date.now()}`;
    const username = currentUser?.displayName || currentUser?.email?.split("@")[0] || "System";
    
    const newUploads = files.map(file => ({
      id: `up-${Date.now()}-${Math.random()}`,
      name: file.name,
      status: "uploading"
    }));

    setUploadingFiles(prev => [...prev, ...newUploads]);

    const uploadPromises = files.map(async (file, idx) => {
      try {
        const attachment = await uploadPartnerAttachment(partnerId, file, "NDA", username);
        setAttachments(prev => [...prev, attachment]);
        setUploadingFiles(prev => prev.map(u => u.name === file.name ? { ...u, status: "success" } : u));
      } catch (err) {
        console.error("Error uploading file:", file.name, err);
        setUploadingFiles(prev => prev.map(u => u.name === file.name ? { ...u, status: "error" } : u));
      }
    });

    await Promise.allSettled(uploadPromises);
    setTimeout(() => {
      setUploadingFiles([]);
    }, 3000);
  };

  const handleRemoveAttachment = async (index, att) => {
    if (!window.confirm(`Are you sure you want to delete attachment "${att.name}"?`)) return;
    try {
      if (att.path) {
        await deletePartnerAttachment(att.path);
      }
      const updated = [...attachments];
      updated.splice(index, 1);
      setAttachments(updated);
    } catch (err) {
      console.error("Error deleting attachment:", err);
      setError("Failed to delete attachment.");
    }
  };

  const handleAttachmentTypeChange = (index, value) => {
    const updated = [...attachments];
    updated[index].type = value;
    setAttachments(updated);
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    if (!name.trim()) {
      setError("Company Name is required.");
      return;
    }
    if (!categoryName) {
      setError("Please select a partner category.");
      return;
    }

    onSave({
      name: name.trim(),
      categoryName,
      pocs: pocs.filter(p => p.name.trim() !== ""),
      attachments
    });
  };

  return (
    <div style={{
      position: "fixed",
      top: 0,
      left: 0,
      right: 0,
      bottom: 0,
      backgroundColor: "rgba(0, 0, 0, 0.75)",
      backdropFilter: "blur(4px)",
      display: "flex",
      alignItems: "center",
      justifyContent: "center",
      zIndex: 1100,
      padding: "1rem"
    }}>
      <div style={{
        backgroundColor: "var(--bg-card)",
        border: "1px solid var(--border-color)",
        borderRadius: "12px",
        width: "100%",
        maxWidth: "750px",
        maxHeight: "90vh",
        display: "flex",
        flexDirection: "column",
        boxShadow: "0 20px 25px -5px rgba(0, 0, 0, 0.5)",
        overflow: "hidden"
      }}>
        {/* Header */}
        <div style={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          padding: "1.25rem 1.5rem",
          borderBottom: "1px solid var(--border-color)",
          backgroundColor: "rgba(255, 255, 255, 0.02)"
        }}>
          <h2 style={{ fontSize: "1.25rem", fontWeight: 600, color: "var(--text-primary)", margin: 0 }}>
            {isEdit ? "Edit Partner" : "Add Partner"}
          </h2>
          <button 
            type="button"
            onClick={onClose} 
            style={{ background: "none", border: "none", color: "var(--text-secondary)", cursor: "pointer" }}
          >
            <X size={20} />
          </button>
        </div>

        {/* Body */}
        <form onSubmit={handleSubmit} style={{ display: "flex", flexDirection: "column", flex: 1, overflow: "hidden" }}>
          <div style={{ padding: "1.5rem", overflowY: "auto", display: "flex", flexDirection: "column", gap: "1.25rem", flex: 1 }}>
            
            {error && <div className="alert alert-danger">{error}</div>}

            {/* General Info */}
            <div style={{ display: "grid", gridTemplateColumns: "1.2fr 0.8fr", gap: "1rem" }}>
              <div className="form-group">
                <label className="form-label" htmlFor="companyName">Company Name *</label>
                <input
                  id="companyName"
                  type="text"
                  required
                  placeholder="Enter partner company name..."
                  className="form-input"
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                />
              </div>

              <div className="form-group">
                <label className="form-label" htmlFor="partnerCategory">Category *</label>
                <select
                  id="partnerCategory"
                  className="form-input"
                  value={categoryName}
                  onChange={(e) => setCategoryName(e.target.value)}
                  style={{ backgroundColor: "var(--bg-card)" }}
                >
                  <option value="">-- Select Category --</option>
                  {categories.map(cat => (
                    <option key={cat.id} value={cat.name}>{cat.name}</option>
                  ))}
                </select>
              </div>
            </div>

            {/* POC List */}
            <div>
              <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: "0.75rem" }}>
                <label className="form-label" style={{ margin: 0 }}>Points of Contact (POCs)</label>
                <button
                  type="button"
                  className="btn btn-secondary"
                  onClick={handleAddPoc}
                  style={{ padding: "0.25rem 0.5rem", fontSize: "0.8rem" }}
                >
                  <Plus size={14} style={{ marginRight: "0.25rem" }} /> Add POC
                </button>
              </div>

              <div style={{ display: "flex", flexDirection: "column", gap: "0.75rem" }}>
                {pocs.map((poc, index) => (
                  <div key={index} style={{ display: "flex", gap: "0.5rem", padding: "0.75rem", border: "1px solid var(--border-color)", borderRadius: "6px", backgroundColor: "rgba(255,255,255,0.01)", position: "relative" }}>
                    <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: "0.5rem", flex: 1 }}>
                      <div className="input-with-icon">
                        <User size={14} className="input-icon" />
                        <input
                          type="text"
                          placeholder="POC Name"
                          className="form-input icon-padding"
                          value={poc.name}
                          onChange={(e) => handlePocChange(index, "name", e.target.value)}
                          style={{ margin: 0, padding: "0.35rem 0.35rem 0.35rem 2.25rem", fontSize: "0.85rem" }}
                        />
                      </div>
                      <div className="input-with-icon">
                        <Briefcase size={14} className="input-icon" />
                        <input
                          type="text"
                          placeholder="POC Title"
                          className="form-input icon-padding"
                          value={poc.title}
                          onChange={(e) => handlePocChange(index, "title", e.target.value)}
                          style={{ margin: 0, padding: "0.35rem 0.35rem 0.35rem 2.25rem", fontSize: "0.85rem" }}
                        />
                      </div>
                      <div className="input-with-icon">
                        <Mail size={14} className="input-icon" />
                        <input
                          type="email"
                          placeholder="POC Email"
                          className="form-input icon-padding"
                          value={poc.email}
                          onChange={(e) => handlePocChange(index, "email", e.target.value)}
                          style={{ margin: 0, padding: "0.35rem 0.35rem 0.35rem 2.25rem", fontSize: "0.85rem" }}
                        />
                      </div>
                      <div className="input-with-icon">
                        <Phone size={14} className="input-icon" />
                        <input
                          type="text"
                          placeholder="POC Phone"
                          className="form-input icon-padding"
                          value={poc.phone}
                          onChange={(e) => handlePocChange(index, "phone", e.target.value)}
                          style={{ margin: 0, padding: "0.35rem 0.35rem 0.35rem 2.25rem", fontSize: "0.85rem" }}
                        />
                      </div>
                    </div>
                    {pocs.length > 1 && (
                      <button
                        type="button"
                        onClick={() => handleRemovePoc(index)}
                        style={{ background: "none", border: "none", color: "var(--danger)", cursor: "pointer", display: "flex", alignItems: "center" }}
                      >
                        <Trash2 size={16} />
                      </button>
                    )}
                  </div>
                ))}
              </div>
            </div>

            {/* Document Containers (File Attachments & Platform Agreements) */}
            <div style={{ border: "1px solid var(--border-color)", borderRadius: "8px", padding: "1rem" }}>
              <label className="form-label" style={{ marginBottom: "0.75rem", display: "block" }}>Contract Documents & Agreements</label>

              {/* Linked Platform Agreements */}
              {linkedPlatformAgreements.length > 0 && (
                <div style={{ marginBottom: "1.25rem", padding: "0.75rem 1rem", border: "1px solid rgba(59, 130, 246, 0.3)", borderRadius: "8px", backgroundColor: "rgba(59, 130, 246, 0.04)" }}>
                  <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: "0.5rem" }}>
                    <label className="form-label" style={{ fontWeight: 700, color: "var(--primary)", margin: 0, display: "flex", alignItems: "center", gap: "0.4rem", fontSize: "0.85rem" }}>
                      <FileText size={16} /> Platform Generated Agreements ({linkedPlatformAgreements.length})
                    </label>
                  </div>
                  <div style={{ display: "flex", flexDirection: "column", gap: "0.5rem" }}>
                    {linkedPlatformAgreements.map((agr) => (
                      <div key={agr.id} style={{ display: "flex", justifyContent: "space-between", alignItems: "center", backgroundColor: "var(--bg-card)", padding: "0.5rem 0.75rem", borderRadius: "6px", border: "1px solid var(--border-color)", gap: "0.75rem" }}>
                        <div>
                          <div style={{ fontWeight: 600, fontSize: "0.85rem", color: "var(--text-primary)" }}>{agr.title}</div>
                          <div style={{ fontSize: "0.72rem", color: "var(--text-secondary)", marginTop: "0.15rem" }}>
                            Template: {agr.templateTitle || "Teaming Agreement"} • Effective: {agr.effectiveDate || "N/A"} • Status: <span style={{ color: "var(--primary)", fontWeight: 600 }}>{agr.status}</span>
                          </div>
                        </div>
                        {onOpenAgreement && (
                          <button
                            type="button"
                            className="btn btn-secondary"
                            onClick={() => {
                              onClose();
                              onOpenAgreement(agr);
                            }}
                            style={{ padding: "0.25rem 0.6rem", fontSize: "0.75rem", whiteSpace: "nowrap" }}
                          >
                            Open Agreement ↗
                          </button>
                        )}
                      </div>
                    ))}
                  </div>
                </div>
              )}

              <div 
                onDragOver={handleDragOver}
                onDragLeave={handleDragLeave}
                onDrop={handleDrop}
                onClick={() => document.getElementById("partner-file-input").click()}
                style={{
                  border: isDragging ? "2px dashed var(--primary)" : "2px dashed var(--border-color)",
                  backgroundColor: isDragging ? "rgba(var(--primary-rgb), 0.05)" : "rgba(255,255,255,0.01)",
                  borderRadius: "8px",
                  padding: "1.5rem",
                  textAlign: "center",
                  cursor: "pointer",
                  marginBottom: "1rem",
                  transition: "all 0.2s ease",
                  display: "flex",
                  flexDirection: "column",
                  alignItems: "center",
                  justifyContent: "center",
                  gap: "0.25rem"
                }}
              >
                <input 
                  id="partner-file-input"
                  type="file" 
                  multiple 
                  onChange={handleFileSelect} 
                  style={{ display: "none" }} 
                />
                <Paperclip size={24} style={{ color: "var(--text-secondary)" }} />
                <span style={{ fontSize: "0.85rem", fontWeight: 500, color: "var(--text-primary)" }}>
                  Drag & drop agreements or contract files here, or <span style={{ color: "var(--primary)", textDecoration: "underline" }}>browse</span>
                </span>
                <span style={{ fontSize: "0.75rem", color: "var(--text-secondary)" }}>
                  Supports NDAs, Teaming Agreements, and general contract PDFs or documents
                </span>
              </div>

              {/* Upload States */}
              {uploadingFiles.length > 0 && (
                <div style={{ display: "flex", flexDirection: "column", gap: "0.35rem", marginBottom: "1rem", padding: "0.5rem", backgroundColor: "rgba(0,0,0,0.15)", borderRadius: "6px" }}>
                  {uploadingFiles.map(u => (
                    <div key={u.id} style={{ display: "flex", justifyContent: "space-between", alignItems: "center", fontSize: "0.75rem" }}>
                      <span style={{ color: "var(--text-secondary)", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap", maxWidth: "70%" }}>{u.name}</span>
                      {u.status === "uploading" ? (
                        <span style={{ color: "var(--primary)" }}>Uploading...</span>
                      ) : u.status === "success" ? (
                        <span style={{ color: "#00e096" }}>Uploaded ✓</span>
                      ) : (
                        <span style={{ color: "var(--danger)" }}>Failed ✗</span>
                      )}
                    </div>
                  ))}
                </div>
              )}

              {/* Attachments List */}
              <div style={{ display: "flex", flexDirection: "column", gap: "0.5rem" }}>
                {attachments.map((att, idx) => (
                  <div key={att.id || idx} style={{ display: "flex", justifyContent: "space-between", alignItems: "center", backgroundColor: "rgba(255,255,255,0.02)", padding: "0.5rem 0.75rem", borderRadius: "6px", border: "1px solid var(--border-color)", gap: "1rem" }}>
                    <div style={{ display: "flex", flexDirection: "column", flex: 1, overflow: "hidden" }}>
                      {att.virusFree === true ? (
                        <a href={att.url} target="_blank" rel="noopener noreferrer" style={{ fontSize: "0.85rem", fontWeight: 500, color: "var(--primary)", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap", display: "inline-flex", alignItems: "center", gap: "0.25rem" }}>
                          <FileText size={14} />
                          {att.name}
                        </a>
                      ) : att.virusFree === false ? (
                        <span style={{ fontSize: "0.85rem", fontWeight: 500, color: "var(--danger)", display: "inline-flex", alignItems: "center", gap: "0.25rem" }}>
                          <FileText size={14} />
                          {att.name} (Virus Detected!)
                        </span>
                      ) : (
                        <span style={{ fontSize: "0.85rem", fontWeight: 500, color: "var(--text-secondary)", display: "inline-flex", alignItems: "center", gap: "0.25rem" }}>
                          <Loader2 size={14} className="animate-spin" />
                          {att.name} (Scanning...)
                        </span>
                      )}
                      <span style={{ fontSize: "0.7rem", color: "var(--text-secondary)" }}>
                        Uploaded by {att.uploadedBy} on {new Date(att.timestamp).toLocaleDateString()}
                      </span>
                    </div>
                    
                    <div style={{ display: "flex", alignItems: "center", gap: "0.5rem" }}>
                      <select
                        className="form-input"
                        value={att.type}
                        onChange={(e) => handleAttachmentTypeChange(idx, e.target.value)}
                        style={{ margin: 0, padding: "0.25rem 0.5rem", fontSize: "0.8rem", height: "auto", minWidth: "140px", backgroundColor: "var(--bg-card)" }}
                      >
                        <option value="NDA">NDA</option>
                        <option value="Teaming Agreement">Teaming Agreement</option>
                        <option value="Other">Other Contract</option>
                      </select>
                      {att.virusFree === true && (
                        <a 
                          href={att.url} 
                          download={att.name} 
                          target="_blank" 
                          rel="noopener noreferrer"
                          style={{ color: "var(--primary)", cursor: "pointer", display: "flex", alignItems: "center", justifyContent: "center" }}
                          title="Download document"
                        >
                          <Download size={16} />
                        </a>
                      )}
                      <button 
                        type="button" 
                        onClick={() => handleRemoveAttachment(idx, att)} 
                        style={{ background: "none", border: "none", color: "var(--danger)", cursor: "pointer", display: "flex", alignItems: "center", justifyContent: "center" }}
                      >
                        <Trash2 size={16} />
                      </button>
                    </div>
                  </div>
                ))}

                {attachments.length === 0 && (
                  <div style={{ textAlign: "center", padding: "0.75rem", color: "var(--text-secondary)", fontStyle: "italic", fontSize: "0.85rem" }}>
                    No documents uploaded.
                  </div>
                )}
              </div>
            </div>

          </div>

          {/* Footer */}
          <div style={{
            display: "flex",
            justifyContent: "flex-end",
            alignItems: "center",
            padding: "1rem 1.5rem",
            borderTop: "1px solid var(--border-color)",
            backgroundColor: "rgba(255, 255, 255, 0.02)",
            gap: "0.75rem"
          }}>
            <button 
              type="button" 
              className="btn btn-secondary" 
              onClick={onClose}
            >
              Cancel
            </button>
            <button 
              type="submit" 
              className="btn btn-primary"
            >
              {isEdit ? "Save Changes" : "Create Partner"}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}
